// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { z } from 'zod';

/**
 * Unit of measure UNECE Recommendation 20 codes.
 * These are the canonical storage values used in the database.
 * See: https://unece.org/trade/uncefact/cl-recommendations
 */
const unitOfMeasureValues = [
  // Count/Grouping (9)
  'EA', // Each
  'H87', // Piece
  'PR', // Pair
  'SET', // Set (not official UNECE, but widely used)
  'C62', // Unit
  'DZN', // Dozen
  'GRO', // Gross
  'KT', // Kit
  'LO', // Lot
  // Packaging (16)
  'BG', // Bag
  'BO', // Bottle
  'BX', // Box
  'CT', // Carton
  'CS', // Case
  'CR', // Crate
  'DR', // Drum
  'EN', // Envelope
  'KG', // Keg (Note: UNECE KG is Keg, not Kilogram - KGM is Kilogram)
  'PK', // Pack
  'PX', // Pallet
  'RL', // Reel
  'RO', // Roll
  'ST', // Sheet
  'SV', // Skid
  'TU', // Tube
  // Length (8)
  'MMT', // Millimeter
  'CMT', // Centimeter
  'MTR', // Meter
  'KMT', // Kilometer
  'INH', // Inch
  'FOT', // Foot
  'YRD', // Yard
  'SMI', // Mile
  // Weight/Mass (7)
  'MGM', // Milligram
  'GRM', // Gram
  'KGM', // Kilogram
  'ONZ', // Ounce
  'LBR', // Pound
  'TNE', // Tonne metric
  'STN', // Ton US
  // Volume (11)
  'MLT', // Milliliter
  'LTR', // Liter
  'OZA', // Fluid ounce
  'PTI', // Pint
  'QTI', // Quart
  'GLL', // Gallon
  'INQ', // Cubic inch
  'DMQ', // Cubic decimeter
  'MTQ', // Cubic meter
  'FTQ', // Cubic foot
  'YDQ', // Cubic yard
  // Area (6)
  'MMK', // Square millimeter
  'CMK', // Square centimeter
  'INK', // Square inch
  'MTK', // Square meter
  'FTK', // Square foot
  'YDK', // Square yard
] as const;

/**
 * Zod schema for valid unit of measure enum values.
 */
export const unitOfMeasureSchema = z.enum(unitOfMeasureValues, {
  errorMap: () => ({ message: 'Please select a valid unit of measure' }),
});

export type UnitOfMeasure = z.infer<typeof unitOfMeasureSchema>;

/**
 * Default unit of measure for new parts.
 */
export const DEFAULT_UNIT_OF_MEASURE: UnitOfMeasure = 'EA';

/**
 * Display names for unit of measure values.
 * Format: "Name (symbol)" for measurement units, "Name" for count/packaging units.
 */
export const UOM_DISPLAY_MAP: Record<UnitOfMeasure, string> = {
  // Count/Grouping (with symbols where applicable)
  EA: 'Each (ea)',
  H87: 'Piece (pc)',
  PR: 'Pair (pr)',
  SET: 'Set',
  C62: 'Unit',
  DZN: 'Dozen (doz)',
  GRO: 'Gross',
  KT: 'Kit',
  LO: 'Lot',
  // Packaging (no symbols)
  BG: 'Bag',
  BO: 'Bottle',
  BX: 'Box',
  CT: 'Carton',
  CS: 'Case',
  CR: 'Crate',
  DR: 'Drum',
  EN: 'Envelope',
  KG: 'Keg',
  PK: 'Pack',
  PX: 'Pallet',
  RL: 'Reel',
  RO: 'Roll',
  ST: 'Sheet',
  SV: 'Skid',
  TU: 'Tube',
  // Length
  MMT: 'Millimeter (mm)',
  CMT: 'Centimeter (cm)',
  MTR: 'Meter (m)',
  KMT: 'Kilometer (km)',
  INH: 'Inch (in)',
  FOT: 'Foot (ft)',
  YRD: 'Yard (yd)',
  SMI: 'Mile (mi)',
  // Weight/Mass
  MGM: 'Milligram (mg)',
  GRM: 'Gram (g)',
  KGM: 'Kilogram (kg)',
  ONZ: 'Ounce (oz)',
  LBR: 'Pound (lb)',
  TNE: 'Tonne metric (t)',
  STN: 'Ton US (NT)',
  // Volume
  MLT: 'Milliliter (mL)',
  LTR: 'Liter (L)',
  OZA: 'Fluid ounce (fl oz)',
  PTI: 'Pint (pt)',
  QTI: 'Quart (qt)',
  GLL: 'Gallon (gal)',
  INQ: 'Cubic inch (in3)',
  DMQ: 'Cubic decimeter (dm3)',
  MTQ: 'Cubic meter (m3)',
  FTQ: 'Cubic foot (ft3)',
  YDQ: 'Cubic yard (yd3)',
  // Area
  MMK: 'Square millimeter (mm2)',
  CMK: 'Square centimeter (cm2)',
  INK: 'Square inch (in2)',
  MTK: 'Square meter (m2)',
  FTK: 'Square foot (ft2)',
  YDK: 'Square yard (yd2)',
};

/**
 * Gets the display name for a unit of measure.
 */

/**
 * Unit of measure option for select components.
 */
interface UnitOfMeasureOption {
  value: UnitOfMeasure;
  label: string;
  group: string;
}

/**
 * Unit of measure options grouped by category for select components.
 */
export const UOM_OPTIONS_BY_CATEGORY = [
  {
    label: 'Count / Grouping',
    options: [
      'EA',
      'H87',
      'PR',
      'SET',
      'C62',
      'DZN',
      'GRO',
      'KT',
      'LO',
    ] as const,
  },
  {
    label: 'Packaging',
    options: [
      'BG',
      'BO',
      'BX',
      'CT',
      'CS',
      'CR',
      'DR',
      'EN',
      'KG',
      'PK',
      'PX',
      'RL',
      'RO',
      'ST',
      'SV',
      'TU',
    ] as const,
  },
  {
    label: 'Length',
    options: ['MMT', 'CMT', 'MTR', 'KMT', 'INH', 'FOT', 'YRD', 'SMI'] as const,
  },
  {
    label: 'Weight / Mass',
    options: ['MGM', 'GRM', 'KGM', 'ONZ', 'LBR', 'TNE', 'STN'] as const,
  },
  {
    label: 'Volume',
    options: [
      'MLT',
      'LTR',
      'OZA',
      'PTI',
      'QTI',
      'GLL',
      'INQ',
      'DMQ',
      'MTQ',
      'FTQ',
      'YDQ',
    ] as const,
  },
  {
    label: 'Area',
    options: ['MMK', 'CMK', 'INK', 'MTK', 'FTK', 'YDK'] as const,
  },
] as const;

/**
 * Flattened unit of measure options with group labels for Autocomplete components.
 */
export const UNIT_OF_MEASURE_OPTIONS: readonly UnitOfMeasureOption[] =
  UOM_OPTIONS_BY_CATEGORY.flatMap((category) =>
    category.options.map((value) => ({
      value,
      label: UOM_DISPLAY_MAP[value],
      group: category.label,
    }))
  );
